import { parse, format, isValid, parseISO } from "date-fns";
import { createToast } from "mosha-vue-toastify";
import "mosha-vue-toastify/dist/style.css";
import { applyMask } from "@/applyMask";
import { ptBR } from "date-fns/locale";
import ptBrLocale from 'date-fns/locale/pt-BR'

const helpers = {
  formatCNPJ(cnpj: string): string {
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  },
  formatCPF(cpf: string): string {
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  },

  formatCpfCnpj(documento: string | number): string {
    if (typeof documento != "string" && typeof documento != "number") {
      documento = "";
    }
    if (typeof documento == "number") {
      documento = documento.toString();
    }
    if (documento.length == 11) {
      return this.formatCPF(documento);
    }
    return this.formatCNPJ(documento);
  },

  formatR$(valor: number | string): string {
    if (typeof valor == "string") {
      valor = parseFloat(valor);
    }
    return valor.toLocaleString("pt-br", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "currency",
      currency: "BRL",
    });
  },
  formatValor(valor: number | string, digits = 2): string {
    if (typeof valor == "string") {
      valor = parseFloat(valor);
    }
    if (typeof valor == "undefined") {
      return "0";
    }
    return valor.toLocaleString("pt-br", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  },
  formatDataWithTimezone(date: Date, formatStr = "dd/MM/yyyy HH:ii"): string {
    if (!date) {
      return "";
    }
    try {
      if (typeof date === "string") {
        return format(parseISO(date), formatStr, {
          locale: ptBR,
        });
      }
      return format(date, formatStr, {
        locale: ptBR,
      });
    } catch (error) {
      console.log("error on format date", date);
      return "";
    }
  },
  formatData(data: string, template = "dd/MM/yyyy"): string {
    if (!data) {
      return "";
    }
    if (typeof data == "undefined") {
      return "";
    }
    if (data == "1900-01-01 00:00:00.000" || data == "1900-01-01 00:00:00") {
      return "";
    }
    if (data.length == 19) {
      data = `${data}.000`;
    }
    if (data.length == 8) {
      data = `${data.replace(/^(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")}`;
    }
    const novaData = parse(data, "yyyy-MM-dd HH:mm:ss.SSS", new Date());
    if (isValid(novaData)) {
      return format(novaData, template);
    }

    const novaData2 = parse(data, "yyyy-MM-dd", new Date());
    if (isValid(novaData2)) {
      return format(novaData2, template);
    }
    return "";
  },
  formatCep(cep: string): string {
    if (cep.length != 8) {
      return "";
    }
    return cep.replace(/^(\d{2})(\d{3})(\d{3})/, "$1.$2-$3");
  },
  formatDataHora(data: string): string {
    if (!data) {
      return "";
    }
    if (typeof data == "undefined") {
      return "";
    }
    if (data == "1900-01-01 00:00:00.000" || data == "1900-01-01 00:00:00") {
      return "";
    }
    if (data.length == 19) {
      data = `${data}.000`;
    }
    const novaData = parse(data, "yyyy-MM-dd HH:mm:ss.SSS", new Date());
    if (isValid(novaData)) {
      return format(novaData, "dd/MM/yyyy HH:mm");
    }
    return "";
  },
  formatPercent(value: string): string {
    return (
      (parseFloat(value) / 100).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + "%"
    );
  },
  formatDataByLocation(data: any, template = "dd/MM/yyyy HH:mm") {
    if (!data) {
      return "";
    }
    return format(new Date(data), template, { locale: ptBrLocale });
  },
  getDataAtual(template = "yyyy-MM-dd") {
    return format(new Date(), template, { locale: ptBrLocale });
  },
  mensagemSucesso(descricao: string, titulo = "Sucesso!"): void {
    createToast(
      {
        title: titulo,
        description: descricao,
      },
      {
        type: "success",
        position: "top-center",
        transition: "bounce",
      }
    );
  },

  mensagemErro(descricao: string, titulo = "Erro!"): void {
    createToast(
      {
        title: titulo,
        description: descricao,
      },
      {
        type: "danger",
        position: "top-center",
        transition: "bounce",
      }
    );
  },

  mensagemAviso(descricao: string, titulo = "Aviso!"): void {
    createToast(
      {
        title: titulo,
        description: descricao,
      },
      {
        type: "warning",
        position: "top-center",
        transition: "bounce",
      }
    );
  },

  formatContato(celular: string) {
    if (!celular) {
      return "";
    }

    const isCelular = celular.length === 11;
    let textoAjustado = "";

    if (isCelular) {
      textoAjustado = applyMask(celular, "(99) 99999-9999");
    } else {
      textoAjustado = applyMask(celular, "(99) 9999-9999");
    }

    return textoAjustado;
  },

  formatDate(date: Date | string, formatStr = "dd/MM/yyyy") {
    if (!date) {
      return "";
    }
    try {
      if (typeof date === "string") {
        return format(parseISO(date), formatStr, {
          locale: ptBR,
        });
      }
      return format(date, formatStr, {
        locale: ptBR,
      });
    } catch (error) {
      console.log("error on format date", date);
      return "";
    }
  },

  formatPlacaVeiculo(placa: string) {
    if (!placa) {
      return "";
    }

    const [a, b, c, d, e, f, g] = placa.split("");

    return `${a}${b}${c}-${d}${e}${f}${g}`;
  },

  openUrl(url: string, target = "_blank") {
    const link = document.createElement('a');
    link.href = url;
    link.target = target;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};

export default helpers;
